/**
 * NotificationsSection Component
 * Push and email notification preferences grid
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { useTranslation } from "@/language"
import { useNotificationSettings, useUpdateNotificationSettings } from "../hooks/useSettings"
import type { NotificationPreference } from "../types/settings.types"

const NOTIFICATION_TYPE_IDS = [
  "ritual_reminders",
  "streak_updates",
  "community_activity",
  "teacher_updates",
  "message_invites",
  "app_announcements",
] as const

type NotificationTypeId = (typeof NOTIFICATION_TYPE_IDS)[number]

/**
 * Map notification type IDs (snake_case, matching the backend) to the
 * camelCase suffix used in the `notifications.*` i18n keys inside
 * `settings.json`. Most IDs follow a simple snake→camel conversion but a
 * few have irregular plurals.
 */
const ID_TO_I18N_SUFFIX: Record<string, string> = {
  ritual_reminders: "ritualReminders",
  streak_updates: "streakUpdates",
  community_activity: "communityActivity",
  teacher_updates: "teacherUpdates",
  message_invites: "messagesInvites",
  app_announcements: "appAnnouncements",
}

function i18nKey(id: string, suffix: "" | "Desc" = ""): string {
  const base = ID_TO_I18N_SUFFIX[id] ?? id
  return `notifications.${base}${suffix}`
}

const DEFAULT_BOOLEANS: Record<NotificationTypeId, { push: boolean; email: boolean }> = {
  ritual_reminders: { push: true, email: true },
  streak_updates: { push: true, email: false },
  community_activity: { push: true, email: true },
  teacher_updates: { push: false, email: true },
  message_invites: { push: true, email: true },
  app_announcements: { push: false, email: true },
}

export interface NotificationsSectionHandle {
  save: () => Promise<void>
}

interface NotificationsSectionProps {
  onDirtyChange?: (dirty: boolean) => void
  saveRef?: React.MutableRefObject<NotificationsSectionHandle | null>
}

export function NotificationsSection({ onDirtyChange, saveRef }: NotificationsSectionProps = {}) {
  const { t } = useTranslation("settings")
  const { data, isLoading } = useNotificationSettings()
  const { mutateAsync: updateNotifications, isPending } = useUpdateNotificationSettings()

  // Compute translated labels and default preferences reactively so the UI
  // updates immediately when the user changes language.
  const defaultPreferences = useMemo<NotificationPreference[]>(
    () =>
      NOTIFICATION_TYPE_IDS.map(id => ({
        id,
        label: t(i18nKey(id)),
        description: t(i18nKey(id, "Desc")),
        push: DEFAULT_BOOLEANS[id].push,
        email: DEFAULT_BOOLEANS[id].email,
      })),
    [t],
  )

  const notificationTypeLabels = useMemo<Record<string, string>>(
    () => Object.fromEntries(NOTIFICATION_TYPE_IDS.map(id => [id, t(i18nKey(id))])),
    [t],
  )

  const [preferences, setPreferences] = useState<NotificationPreference[]>(() => defaultPreferences)
  const [originalPreferences, setOriginalPreferences] = useState<NotificationPreference[] | null>(
    null,
  )
  const onDirtyChangeRef = useRef(onDirtyChange)
  onDirtyChangeRef.current = onDirtyChange

  const hasUnsavedChanges =
    originalPreferences !== null &&
    JSON.stringify(preferences) !== JSON.stringify(originalPreferences)

  useEffect(() => {
    if (data?.settings && data.settings.length > 0) {
      const serverPrefs = data.settings.map(s => {
        const defaultPref = defaultPreferences.find(d => d.id === s.type)
        return {
          id: s.type,
          label: notificationTypeLabels[s.type] || s.type,
          description: defaultPref?.description || "",
          push: s.sendPushNotification,
          email: s.sendEmailNotification,
        }
      })
      setPreferences(serverPrefs)
      setOriginalPreferences(serverPrefs)
    }
  }, [data, defaultPreferences, notificationTypeLabels])

  // When language changes and data hasn't loaded yet (or is empty), update
  // the initial preferences to reflect the new language.
  useEffect(() => {
    if (!data?.settings?.length && !originalPreferences) {
      setPreferences(defaultPreferences)
    }
  }, [defaultPreferences, data, originalPreferences])

  useEffect(() => {
    onDirtyChangeRef.current?.(hasUnsavedChanges)
  }, [hasUnsavedChanges])

  const handleToggle = (id: string, channel: "push" | "email") => {
    setPreferences(prev =>
      prev.map(pref => (pref.id === id ? { ...pref, [channel]: !pref[channel] } : pref)),
    )
  }

  const handleSave = useCallback(async () => {
    const settings = preferences.map(pref => ({
      type: pref.id,
      sendPushNotification: pref.push,
      sendEmailNotification: pref.email,
    }))
    await updateNotifications(settings)
  }, [preferences, updateNotifications])

  // Expose save function to parent via ref
  useEffect(() => {
    if (saveRef) {
      saveRef.current = { save: handleSave }
    }
    return () => {
      if (saveRef) {
        saveRef.current = null
      }
    }
  }, [saveRef, handleSave])

  const handleSaveClick = () => {
    const settings = preferences.map(pref => ({
      type: pref.id,
      sendPushNotification: pref.push,
      sendEmailNotification: pref.email,
    }))
    updateNotifications(settings)
  }

  const handleReset = () => {
    if (originalPreferences) {
      setPreferences(originalPreferences)
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-8">
        <div>
          <h1 className="text-lg md:text-xl font-semibold text-primary leading-5 uppercase section-heading">
            {t("notifications.title")}
          </h1>
          <p className="text-sm text-primary mt-2.5 md:mt-1.25">{t("notifications.loading")}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-7 w-full">
      <div className="flex flex-col gap-1.25">
        <h1 className="text-lg md:text-xl font-semibold text-primary leading-5 uppercase section-heading">
          {t("notifications.title")}
        </h1>
        <div className="grid grid-cols-[1fr_40px_40px] gap-4 border-0 justify-between w-full">
          <span className="text-sm leading-6.25 text-primary">
            {t("notifications.description")}
          </span>
          <span className="text-sm text-primary text-center">{t("notifications.push")}</span>
          <span className="text-sm text-primary text-center">{t("notifications.email")}</span>
        </div>
      </div>

      <div className="flex flex-col gap-5 w-full">
        {preferences.map(pref => (
          <div
            key={pref.id}
            className="grid grid-cols-[1fr_40px_40px] gap-2 border-0 justify-between w-full"
          >
            <div>
              <p className="text-sm font-semibold text-primary">{pref.label}</p>
              <p className="text-sm text-primary mt-1.25">{pref.description}</p>
            </div>
            <div className="flex justify-center">
              <Checkbox
                checked={pref.push}
                onCheckedChange={() => handleToggle(pref.id, "push")}
                aria-label={`${pref.label} push notification`}
              />
            </div>
            <div className="flex justify-center">
              <Checkbox
                checked={pref.email}
                onCheckedChange={() => handleToggle(pref.id, "email")}
                aria-label={`${pref.label} email notification`}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-5 mt-0.5">
        <Button
          variant="primary"
          size="md"
          className="py-3.75 w-[220px] h-12.5 uppercase"
          onClick={handleSaveClick}
          disabled={!hasUnsavedChanges}
          isLoading={isPending}
        >
          {t("notifications.save")}
        </Button>
        <button
          type="button"
          onClick={handleReset}
          disabled={!hasUnsavedChanges}
          className={`text-sm font-medium transition-colors ${
            hasUnsavedChanges
              ? "text-primary hover:text-primary/80 cursor-pointer"
              : "text-primary/20 cursor-not-allowed"
          }`}
        >
          {t("notifications.reset")}
        </button>
      </div>
    </div>
  )
}
